import axios from 'axios';
import { API_URL, securityHeaders } from '../Constants';
import { notifySessionExpired, EXPIRY_REASON } from './session';

/**
 * The single place that knows the Backend API Contract.
 *
 * Reference: DPA-FE `docs/APICall/BackendAPIContract.md`
 * Deviations: DPA-FE `docs/APICall/DPA-CONFORMANCE.md`
 *
 * Every call goes through here so that the envelope rules, the error-message
 * precedence and the pagination shape exist once rather than being re-derived
 * per hook. Components must never call `axios` directly.
 */

/* -------------------------------------------------------------------------- */
/*  Envelope                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Contract §3: an HTTP 200 carrying `success: false` is a FAILURE, and so is a
 * 200 that omits `success` entirely — `undefined` is falsy. Never trust the
 * status code alone.
 *
 * Contract §7: `error` wins over `message` when building what the user reads.
 */
export const assertApiSuccess = (response) => {
  const body = response?.data;

  if (!body || body.success !== true) {
    const message =
      body?.error || body?.message || 'حدث خطأ أثناء تنفيذ العملية';
    const err = new Error(message);
    err.errorCode = body?.errorCode ?? null;
    // Field-level validation map, key = request field name (§7.2).
    err.errors = body?.errors ?? null;
    err.status = response?.status ?? null;
    throw err;
  }

  return body;
};

/** The payload, unwrapped. `data` is the entity for get-by-id (§5.2). */
export const unwrap = (response) => assertApiSuccess(response).data ?? null;

/**
 * Axios rejects on 4xx/5xx before `assertApiSuccess` can run, so the same
 * precedence has to be applied to the rejection too — otherwise a 400 surfaces
 * as "Request failed with status code 400" instead of the reason the backend
 * wrote for the user.
 */
const rethrow = (error) => {
  const body = error?.response?.data;
  const status = error?.response?.status ?? null;

  // Contract §4.3 — 401 means "your token is no longer valid", 403 means "you
  // are known but not allowed". Only the first is a session problem. Treating
  // 403 as expiry would sign an operator out for opening a screen their role
  // simply does not cover, which is both wrong and impossible to diagnose.
  //
  // Raised as an event rather than redirecting from here: this module has no
  // business navigating, and the guard that listens can keep the operator on
  // the page instead of throwing away whatever they were part-way through.
  if (status === 401) {
    notifySessionExpired(EXPIRY_REASON.UNAUTHORIZED);
  }

  const message =
    body?.error ||
    body?.message ||
    error?.message ||
    'حدث خطأ أثناء تنفيذ العملية';
  const err = new Error(message);
  err.errorCode = body?.errorCode ?? null;
  err.errors = body?.errors ?? null;
  err.status = status;
  throw err;
};

const request = (config) =>
  axios({ headers: securityHeaders, ...config }).catch(rethrow);

/* -------------------------------------------------------------------------- */
/*  Pagination                                                                 */
/* -------------------------------------------------------------------------- */

const buildQuery = (params = {}) => {
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    usp.append(key, String(value));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
};

/**
 * GET a list — pagination in the PATH, filters in the QUERY:
 *
 *   GET /{resource}/{page}/{pageSize}?searchText=&sortBy=&sortDirection=&<filters>
 *
 *   apiGetList('ContactUs', { page: 1, pageSize: 20, searchText: 'umair' })
 *     -> GET /api/ContactUs/1/20?searchText=umair
 *
 * `page` IS ONE-BASED ON THE DPA API. The first page is 1, so the first request
 * a screen makes is `/1/10`, not `/0/10`.
 *
 * This is a DELIBERATE, DOCUMENTED DEVIATION from the signed contract, which
 * says the opposite ("§5.1 … Zero-based. First page is 0, not 1"). That
 * document was written for the training-hub product; the DPA API is 1-based and
 * the backend team is the authority on their own service. Recorded in
 * `DPA-FE/docs/APICall/DPA-CONFORMANCE.md`; do not "correct" it back to
 * zero-based against the live API, which is how a list ends up silently
 * skipping its first page of rows.
 *
 * `pageSize: 0` still means "return all" — that part is unchanged.
 *
 * Always resolves to `{ items, totalCount, currentPage, pageSize }` with `items`
 * an array: never null, never absent.
 *
 * NO PER-RESOURCE EXCEPTIONS. An earlier version of this file kept a set of
 * "resources whose list route does not accept path pagination" and silently
 * called the flat path for them, paginating in the browser. That hid a backend
 * gap behind client code: the network tab showed a non-conforming request and
 * nothing in the UI said why. If a resource 404s here, that is the correct and
 * visible outcome — fix the route, do not special-case the client.
 */
export const apiGetList = async (
  resource,
  { page = 1, pageSize = 0, searchText, segments = [], ...filters } = {}
) => {
  /**
   * Extra PATH segments after `{page}/{pageSize}`.
   *
   * ⚠ A DEVIATION THIS API REQUIRES. Contract §5.1 puts pagination in the
   * path and every filter in the QUERY STRING, and this helper was built to
   * do exactly that. `Pages` does not follow it: its discriminator is a
   * THIRD PATH SEGMENT, and the query-string form does not exist.
   *
   *   GET /api/Pages/0/0/1      ✅ 401 — the route exists
   *   GET /api/Pages/0/0?type=1 ❌ 404 — no such route
   *   (measured 2026-09-29; the opposite was true on 2026-09-25 — see
   *    DPA-CONFORMANCE.md, this endpoint has flip-flopped once already)
   *
   * Use this ONLY where the backend leaves no choice, and record it in
   * DPA-CONFORMANCE.md. A genuine filter still belongs in `filters`.
   *
   * `0` is a MEANINGFUL segment value here (`PageType.Page`), so the guard
   * tests for null/undefined/'' rather than falsiness — dropping a zero
   * would silently request the wrong list.
   */
  const trail = (Array.isArray(segments) ? segments : [segments])
    .filter((segment) => segment !== undefined && segment !== null && segment !== '')
    .map((segment) => encodeURIComponent(String(segment)))
    .join('/');

  const url = `${API_URL}/api/${resource}/${page}/${pageSize}${
    trail ? `/${trail}` : ''
  }${buildQuery({
    searchText,
    ...filters,
  })}`;

  const body = unwrap(await request({ method: 'GET', url }));

  // Contract §5.1 says `data.items`; a resource may still answer with a bare
  // array. Both are accepted so callers never have to care.
  const items = Array.isArray(body?.items)
    ? body.items
    : Array.isArray(body)
      ? body
      : [];

  return {
    items,
    totalCount: body?.totalCount ?? items.length,
    // The contract says `currentPage`; this API sends `pageNumber`. Accept
    // either so the rename can happen backend-side without a client change.
    // Falls back to the 1-based page that was requested.
    currentPage: body?.currentPage ?? body?.pageNumber ?? page,
    pageSize: body?.pageSize ?? pageSize,
  };
};

/**
 * GET a path that is NOT a paginated list — a settings document, a lookup, a
 * computed summary. Returns `data` as-is.
 *
 * Use this rather than `apiGetList` for anything without `{page}/{pageSize}`
 * semantics: `apiGetList` appends the pagination segments to the path, so
 * `apiGetList('ContactUs/Settings')` would request
 * `/api/ContactUs/Settings/0/0` and 404.
 */
export const apiGet = async (path, params) =>
  unwrap(
    await request({
      method: 'GET',
      url: `${API_URL}/api/${path}${buildQuery(params)}`,
    })
  );

/**
 * GET a body that is NOT enveloped.
 *
 * ⚠ AN EXCEPTION, NOT A SHORTCUT. Some controllers predate the Backend API
 * Contract and answer with the payload at the top level, no
 * `{ success, message, error, data }` around it. Measured 2026-09-25:
 *
 *   GET /api/Degree/GetDegrees/200/1
 *     -> { items, totalCount, pageNumber, pageSize }      // no envelope
 *
 * `apiGetList` asserts `success === true` and would throw on that body, so
 * those endpoints need this door. It still routes through `request`, which
 * means an HTTP error is still normalised by `rethrow` and a 401 still
 * raises session-expiry — which calling `axios` directly (as the older hooks
 * do) does NOT.
 *
 * DO NOT use this for new endpoints. Every endpoint written to the contract
 * belongs on `apiGet` / `apiGetList`; reaching for this one hides a backend
 * conformance bug instead of reporting it. Listed in
 * DPA-FE `docs/APICall/DPA-CONFORMANCE.md`.
 */
export const apiGetRaw = async (path, params) =>
  (
    await request({
      method: 'GET',
      url: `${API_URL}/api/${path}${buildQuery(params)}`,
    })
  )?.data ?? null;

/** GET one. Contract §5.2 — `data` is the entity itself, not wrapped again. */
export const apiGetById = async (resource, id) =>
  unwrap(await request({ method: 'GET', url: `${API_URL}/api/${resource}/${id}` }));

/** POST create. Contract §5.3 — returns `data`, which carries the new id. */
export const apiPost = async (path, payload) =>
  unwrap(await request({ method: 'POST', url: `${API_URL}/api/${path}`, data: payload }));

/** PUT. Also used for action endpoints such as `ContactUs/{id}/read` (§5.8). */
export const apiPut = async (path, payload = {}) =>
  unwrap(await request({ method: 'PUT', url: `${API_URL}/api/${path}`, data: payload }));

/** PATCH. Contract §5.5 — absent fields are left untouched server-side. */
export const apiPatch = async (path, payload = {}) =>
  unwrap(await request({ method: 'PATCH', url: `${API_URL}/api/${path}`, data: payload }));

/** DELETE. Contract §5.6 — idempotent; 409 when blocked by a business rule. */
export const apiDelete = async (resource, id) =>
  unwrap(await request({ method: 'DELETE', url: `${API_URL}/api/${resource}/${id}` }));

/**
 * Multipart upload. Contract §5.9 — `Content-Type` is deliberately NOT set so
 * the browser can add the multipart boundary.
 */
export const apiUpload = async (path, formData) => {
  const { 'Content-Type': _ignored, ...headers } = securityHeaders || {};
  return unwrap(
    await request({
      method: 'POST',
      url: `${API_URL}/api/${path}`,
      data: formData,
      headers,
    })
  );
};
